import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { AutoPaySettingRow } from './autopay-setting-row';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { getPaymentMethodFromPaymentMethodOption } from '@/utils/payments';
import { MappedAutoPaySetting } from '@/hooks/payments/use-autopay-settings';
import { PaymentMethod } from '@/enums';
import { PaymentMethodBanner } from './payment-method-banner';
import { PaymentMethodOption } from '@/contexts/payments-context';
import { Platform, StyleProp, Text, View, ViewStyle } from 'react-native';
import { TYPOGRAPHY } from '@/styles';

interface Props {
  autoPaySelfServeEnabled: boolean;
  availableAutoPaySettings: MappedAutoPaySetting[];
  containerStyle?: StyleProp<ViewStyle>;
  hasSavedAutoPaySetting: boolean;
  onChangePaymentMethod: () => void;
  onSaveAutoPaySetting: (autoPaySettingUuid: string) => void;
  onSelectAutoPaySetting: (autoPaySettingUuid: string) => void;
  savingAutoPaySetting: boolean;
  selectedAutoPaySettingUUID?: string;
  selectedPaymentMethod: PaymentMethodOption;
  shouldSaveAutoPaySetting: boolean;
}

export const AutoPaySettingsContainer: React.FC<Props> = ({
  autoPaySelfServeEnabled,
  availableAutoPaySettings,
  containerStyle,
  hasSavedAutoPaySetting,
  onChangePaymentMethod,
  onSelectAutoPaySetting,
  onSaveAutoPaySetting,
  savingAutoPaySetting,
  selectedAutoPaySettingUUID,
  selectedPaymentMethod,
  shouldSaveAutoPaySetting,
}) => {
  const [styles] = useStyles();

  const paymentMethod: PaymentMethod = useMemo(() => {
    return getPaymentMethodFromPaymentMethodOption(selectedPaymentMethod);
  }, [selectedPaymentMethod]);

  return (
    <View style={[styles.container, containerStyle]}>
      <PaymentMethodBanner
        onChangePaymentMethod={onChangePaymentMethod}
        paymentMethod={paymentMethod}
        selectedPaymentMethod={selectedPaymentMethod}
      />
      {availableAutoPaySettings.map(
        ({ frequency, description, uuid }, index) => (
          <AutoPaySettingRow
            description={description}
            disabled={!autoPaySelfServeEnabled}
            frequency={frequency}
            key={`autopay-setting-${uuid}`}
            onSelectAutoPaySetting={onSelectAutoPaySetting}
            rowStyle={[
              index === availableAutoPaySettings.length - 1 &&
                styles.autoPayOptionEndRow,
            ]}
            selected={uuid === selectedAutoPaySettingUUID}
            uuid={uuid}
          />
        ),
      )}
      {hasSavedAutoPaySetting ? (
        <Text style={styles.contactDistributorDisclaimer}>
          {i18n.t('autopay.contact_distributor')}
        </Text>
      ) : null}
      {autoPaySelfServeEnabled && selectedAutoPaySettingUUID ? (
        <Button
          disabled={!shouldSaveAutoPaySetting}
          loading={savingAutoPaySetting}
          onPress={() => {
            onSaveAutoPaySetting(selectedAutoPaySettingUUID);
          }}
          style={[
            styles.saveButton,
            Platform.OS !== 'web' ? styles.saveButtonMobile : undefined,
          ]}
          trackingData={null}
        >
          {i18n.t('save')}
        </Button>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  autoPayOptionEndRow: {
    borderBottomWidth: 0,
  },
  contactDistributorDisclaimer: {
    ...TYPOGRAPHY.small,
    backgroundColor: theme.colors.background,
    borderRadius: 8,
    color: theme.colors.primary,
    lineHeight: 21,
    marginBottom: 32,
    padding: 8,
    textAlign: 'center',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 8,
  },
  saveButton: {
    alignSelf: 'center',
    paddingHorizontal: 24,
  },
  saveButtonMobile: {
    width: '100%',
  },
}));
